import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SectionHeading, ContactForm } from '../components';
import PublicRegistrationForm from '../components/contact/PublicRegistrationForm';
import { useReunion } from '../contexts/ReunionContext';

// ============ ICONS ============
const ChevronDown = ({ open }: { open: boolean }) => (
  <svg
    className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

// ============ DATA ============
interface ContactInfoItem {
  icon: JSX.Element;
  label: string;
  value: string;
  detail?: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

const contactInfo: ContactInfoItem[] = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
      </svg>
    ),
    label: 'Email',
    value: 'info@kgprodesign.com',
    detail: 'For registration help or general questions',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
      </svg>
    ),
    label: 'Phone',
    value: '(407) 900-7724',
    detail: 'Available weekdays 9am-5pm EST',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    label: 'Response Time',
    value: 'Within 48 hours',
    detail: 'We do our best to reply quickly',
  },
];

const faqs: FaqItem[] = [
  {
    question: 'How do I register for the reunion?',
    answer:
      'Visit the Register page to create your account, provide your details, and you will receive a verification email to complete your registration.',
  },
  {
    question: 'I forgot my password. How do I reset it?',
    answer:
      'On the Login page, click "Forgot Password" and enter your email address. You will receive a link to reset your password.',
  },
  {
    question: 'Can I register multiple family members at once?',
    answer:
      'Yes. Once you are registered and logged in, you can add additional family members through your dashboard. Group leaders can manage their entire family unit.',
  },
  {
    question: 'What payment methods are accepted?',
    answer:
      'We accept all major credit and debit cards through our secure payment system. All payments are processed by KG ProDesign LLC and will appear on your statement as "KG PRODESIGN LLC" or "KGP". Service fees may apply. See our Terms & Conditions for details.',
  },
  {
    question: 'What is the refund policy?',
    answer:
      'Full refunds are available for cancellation requests made 90 or more days before the event start date. After the 90-day window, refunds may be partial or unavailable. Visit our Cancellation & Refund Policy page for complete details.',
  },
  {
    question: 'Is the venue accessible for family members with mobility needs?',
    answer:
      'Yes. Please note any accessibility requirements during registration so we can assign appropriate room accommodations and ensure all event spaces are accessible.',
  },
  {
    question: 'Can I update my registration after submitting?',
    answer:
      'Yes. Log in to your account and visit the registration section of your dashboard to update your details, room accommodations, or dietary requirements at any time before the deadline.',
  },
];

// ============ FAQ ACCORDION ITEM ============
const FaqAccordionItem = ({ item }: { item: FaqItem }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="text-lg font-bold text-slate-900 pr-4">{item.question}</span>
        <ChevronDown open={open} />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="px-6 pb-6 text-slate-600 leading-relaxed">{item.answer}</p>
      </div>
    </div>
  );
};

// ============ MESSAGE PANEL (existing two-column contact form + info) ============
const MessagePanel = () => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
    {/* Left: Full Contact Form */}
    <div>
      <span className="eyebrow block mb-4">Say Hello</span>
      <h2 className="heading-section text-slate-900 mb-3">Send Us a Message</h2>
      <p className="text-slate-600 leading-relaxed mb-8">
        Your message goes straight to the reunion organizers. We'll get back to you within a day or two.
      </p>
      <ContactForm variant="full" />
    </div>

    {/* Right: Contact Info */}
    <div className="flex flex-col gap-10">
      <div className="space-y-6">
        <h3 className="text-2xl font-bold text-slate-900">Other Ways to Reach Us</h3>
        <p className="text-slate-600 leading-relaxed">
          Prefer to reach out directly? Here's how to get in touch with the reunion planning team.
        </p>
        {contactInfo.map((item) => (
          <div key={item.label} className="flex items-start gap-4">
            <div className="info-icon">{item.icon}</div>
            <div>
              <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-0.5">
                {item.label}
              </p>
              <p className="text-slate-700 font-medium">{item.value}</p>
              {item.detail && <p className="text-sm text-slate-500">{item.detail}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const tabButtonClasses = (active: boolean) =>
  `px-6 py-3 font-bold text-sm border-b-2 transition-colors ${
    active ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'
  }`;

// ============ COMPONENT ============
type TabKey = 'message' | 'register';

const Contact = () => {
  const { reunion } = useReunion();
  const publicRegEnabled = reunion?.featureToggles?.publicRegistrationEnabled === true;
  const [activeTab, setActiveTab] = useState<TabKey>('message');
  const location = useLocation();
  const navigate = useNavigate();

  // Deep-link support: both /contact#register (hash) and /contact?tab=register (query)
  // open the registration tab directly. Query param is the email-client-safe fallback
  // since some clients strip URL hashes.
  useEffect(() => {
    const wantsRegister =
      location.hash === '#register' ||
      new URLSearchParams(location.search).get('tab') === 'register';
    if (wantsRegister && publicRegEnabled) {
      setActiveTab('register');
      document.querySelector('[role="tablist"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash, location.search, publicRegEnabled]);

  // Reflect the active tab in the URL so a clicked tab is copy-paste shareable.
  const selectTab = (tab: TabKey) => {
    setActiveTab(tab);
    navigate(tab === 'register' ? '/contact?tab=register' : '/contact', { replace: true });
  };

  return (
  <div className="bg-background">
    {/* ===== SECTION 1: PAGE HERO ===== */}
    <section className="relative py-24 lg:py-32 bg-slate-900 overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15" style={{ backgroundImage: "url('/images/goldson-wade-2018-hero.jpg')" }} />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800/90 to-primary-dark/85" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 mb-6">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-xs font-bold tracking-widest uppercase text-white">Contact</span>
        </span>
        <h1 className="heading-hero text-white mb-6 max-w-4xl mx-auto">Got Questions? We've Got Answers</h1>
        <p className="text-lg text-slate-200 leading-relaxed max-w-2xl mx-auto">
          Whether you need help with registration, have questions about the reunion, or just want
          to say hey — drop us a line. We're family, and we're here to help.
        </p>
      </div>
    </section>

    {/* ===== SECTION 2: CONTACT FORM + INFO (Two-Column) ===== */}
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {publicRegEnabled ? (
          <>
            <div role="tablist" aria-label="Contact options" className="flex border-b border-slate-200 mb-12">
              <button
                role="tab"
                aria-selected={activeTab === 'message'}
                aria-controls="panel-message"
                onClick={() => selectTab('message')}
                className={tabButtonClasses(activeTab === 'message')}
              >
                Send a Message
              </button>
              <button
                role="tab"
                aria-selected={activeTab === 'register'}
                aria-controls="panel-register"
                onClick={() => selectTab('register')}
                className={tabButtonClasses(activeTab === 'register')}
              >
                Register a Family
              </button>
            </div>
            <div id="panel-message" role="tabpanel" hidden={activeTab !== 'message'}>
              <MessagePanel />
            </div>
            <div id="panel-register" role="tabpanel" hidden={activeTab !== 'register'}>
              <div className="max-w-3xl">
                <span className="eyebrow block mb-4">Family Registration</span>
                <h2 className="heading-section text-slate-900 mb-3">Register a Family</h2>
                <p className="text-slate-600 leading-relaxed mb-8">
                  Submit your family below. Our team will review your registration and follow up within
                  3 business days with next steps.
                </p>
                <PublicRegistrationForm />
              </div>
            </div>
          </>
        ) : (
          <MessagePanel />
        )}
      </div>
    </section>

    {/* ===== SECTION 3: FAQ ===== */}
    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="FAQ"
          title="Frequently Asked Questions"
          subtitle="Find answers to common questions about registration, payments, and the reunion."
        />
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq) => (
            <FaqAccordionItem key={faq.question} item={faq} />
          ))}
        </div>
      </div>
    </section>
  </div>
  );
};

export default Contact;
